import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Papa from "papaparse";
import { Box, TableCell, TableRow, Typography, TableSortLabel, FormControlLabel, Switch } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";

import TableWrapper from "components/TableWrapper";
import Loader from "components/Loader";
import ButtonV1 from "components/ButtonV1";
import useToaster from "hooks/useToaster";
import {
  fetchAvailabilityData,
  getAvailabilityCellValue,
} from "services/availabilityService";
import { downloadCsv } from "utils";
import { AVAILABILITY_DASHBOARD_COLUMNS } from "constants/availabilityConfig";
import AvailabilityFilters from "../AvailabilityFilters";

const PAGE_SIZE = 20;

const DEFAULT_FILTERS = {
  qty_type: "physical_inventory",
  qty_condition: "No criteria",
};

const AvailabilityGrid = () => {
  const theme = useTheme();
  const triggerToaster = useToaster();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [filterErrors, setFilterErrors] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  const [hidePlannedDerived, setHidePlannedDerived] = useState(false);

  const loadData = useCallback(async (filterParams, pagination, sortParams, hidePlanned) => {
    setIsLoading(true);
    const payload = {
      ...filterParams,
      hide_derived_planned: hidePlanned,
      page: pagination.page,
      size: pagination.size,
    };
    if (sortParams?.sortBy) {
      const colConfig = AVAILABILITY_DASHBOARD_COLUMNS.find(c => c.field === sortParams.sortBy);
      payload.sort_by = colConfig?.sortField || sortParams.sortBy;
      payload.sort_order = sortParams.sortOrder;
    }
    const result = await fetchAvailabilityData(payload);
    if (result.error) {
      triggerToaster(result.error, "error");
      setRows([]);
    } else {
      setRows(result.rows);
      setTotalRows(result.metadata?.total_elements ?? 0);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadData(appliedFilters, { page, size: rowsPerPage }, { sortBy, sortOrder }, hidePlannedDerived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, page, rowsPerPage, sortBy, sortOrder, hidePlannedDerived]);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPage(0);
  };

  const handleFilterChange = ({ name }, value) => {
    let finalValue = value;
    let extraUpdates = {};
    if ((name === "qty_type" || name === "qty_condition") && !value) {
      finalValue = DEFAULT_FILTERS[name];
    }
    // Automatically reset condition if QTY type is cleared
    if (name === "qty_type" && !value) {
      extraUpdates.qty_condition = DEFAULT_FILTERS["qty_condition"];
    }
    setFilters((prev) => ({ ...prev, [name]: finalValue, ...extraUpdates }));
    if (filterErrors[name])
      setFilterErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const handleFilterBlur = ({ name }, value) => {
    if (name === "mm_ids" && value) {
      const MMID_REGEX = /^\d{4}-\d{5}-\d{8}$/;
      const mmids = String(value).split(",").map((s) => s.trim()).filter(Boolean);
      const invalid = mmids.find((mmid) => !MMID_REGEX.test(mmid));
      if (invalid) {
        setFilterErrors((prev) => ({
          ...prev,
          [name]: `Invalid MMid format: ${invalid} (e.g. 1000-10000-10013298)`,
        }));
      }
    } else if (name === "mm_description" && value) {
      if (value.trim().length < 3) {
        setFilterErrors((prev) => ({
          ...prev,
          [name]: "Please enter minimum 3 characters to perform search",
        }));
      }
    }
  };

  const handleApplyFilters = useCallback((payload) => {
    setAppliedFilters(payload);
    setPage(0);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setFilterErrors({});
    setSortBy(null);
    setSortOrder("asc");
    setPage(0);
  }, []);

  const displayColumns = !hidePlannedDerived 
    ? AVAILABILITY_DASHBOARD_COLUMNS 
    : AVAILABILITY_DASHBOARD_COLUMNS.filter(c => c.field !== 'planned_inventory' && c.field !== 'es_derived');

  const handleExport = async () => {
    setIsExporting(true);
    const result = await fetchAvailabilityData({
      ...appliedFilters,
      hide_derived_planned: hidePlannedDerived,
      page: 0,
      size: -1,
    });
    if (result.error) {
      triggerToaster(result.error, "error");
    } else if (result.rows.length) {
      const exportData = result.rows.map((row) => {
        const exportRow = {};
        displayColumns.forEach((col) => {
          if (col.headerName === "Lead Time") {
            exportRow["Units"] = row?.uom?.ui_label_quantity || row?.uom?.name || "-";
          }
          exportRow[col.headerName] = getAvailabilityCellValue(col.field, row, true);
        });
        if (!("Units" in exportRow)) {
          exportRow["Units"] = row?.uom?.ui_label_quantity || row?.uom?.name || "-";
        }
        return exportRow;
      });
      const csv = Papa.unparse(exportData);
      downloadCsv(
        csv,
        `availability_${moment().format("YYYY-MM-DD_HH-mm-ss")}`,
      );
      triggerToaster("Data exported successfully", "success");
    } else {
      triggerToaster("No data to export", "info");
    }
    setIsExporting(false);
  };

  // ─── Table rendering ─────────────────────────────────────────────────────────

  const tableHeader = displayColumns.map((col) => {
    const isQtyField = [
      "physical_inventory",
      "planned_inventory",
      "es_derived",
      "reserved",
      "available_qty",
    ].includes(col.field);

    return (
      <TableCell
        key={col.field}
        sx={{
          fontWeight: 700,
          whiteSpace: "nowrap",
          backgroundColor: theme.palette.bgColor,
          top: 113,
          zIndex: 9,
        }}
      >
        {isQtyField ? (
          <TableSortLabel
            active={sortBy === col.field}
            direction={sortBy === col.field ? sortOrder : "asc"}
            onClick={() => handleSort(col.field)}
          >
            {col.headerName}
          </TableSortLabel>
        ) : (
          col.headerName
        )}
      </TableCell>
    );
  });

  const tableBody = isLoading
    ? [
        <TableRow key="loading">
          <TableCell
            colSpan={displayColumns.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Loader size={28} sx={{ marginTop: 0 }} />
          </TableCell>
        </TableRow>,
      ]
    : rows.length === 0
    ? [
        <TableRow key="empty">
          <TableCell
            colSpan={displayColumns.length}
            align="center"
            sx={{ py: 4 }}
          >
            <Typography color="textSecondary">No data found</Typography>
          </TableCell>
        </TableRow>,
      ]
    : rows.map((row, idx) => (
        <TableRow key={row._id ?? idx} hover>
          {displayColumns.map((col) => (
            <TableCell key={col.field} sx={{ fontSize: "0.85rem" }}>
              {getAvailabilityCellValue(col.field, row)}
            </TableCell>
          ))}
        </TableRow>
      ));

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: theme.palette.accent.main, mb: 3 }}
      >
        Availability Dashboard
      </Typography>

      <AvailabilityFilters
        filters={filters}
        errors={filterErrors}
        onFilterChange={handleFilterChange}
        onFilterBlur={handleFilterBlur}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={hidePlannedDerived}
              onChange={(e) => setHidePlannedDerived(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Hide Planned and Derived Inventory</Typography>}
        />
        <ButtonV1
          title={isExporting ? "Exporting..." : "Export to CSV"}
          onClick={handleExport}
          disabled={isExporting}
          loader={isExporting}
          startIcon={<DownloadIcon />}
          size="small"
          style={{ textTransform: "none", fontWeight: 600 }}
        />
      </Box>

      <TableWrapper
        tableHeader={tableHeader}
        tableBody={tableBody}
        isPagination
        count={totalRows}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={(_, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        isSticky
      />
    </Box>
  );
};

export default AvailabilityGrid;
